import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('abba-language');
      return saved || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('abba-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  en: {
    nav: {
      home: 'Home',
      inHome: 'In-Home Massage',
      studio: 'Studio Services',
      energy: 'Energy Work',
      regressions: 'Past Life Regression',
      janzu: 'Janzu',
      events: 'Events',
      hosts: 'For Hosts',
      community: 'Community',
      store: 'ABBA Store',
      about: 'About',
      contact: 'Contact',
      book: 'Book Now',
      services: 'Services',
      therapies: 'Therapies'
    },
    hero: {
      tagline: 'More than a massage.',
      headline: 'A journey to wellness.',
      subheadline: 'Release tension. Calm your nervous system. Restore balance.',
      cta: 'Book Your Session'
    },
    home: {
      servicesTitle: 'Our Experiences',
      servicesSubtitle: 'Discover the path to your wellbeing',
      inHomeTitle: 'In-Home Massage',
      inHomeDesc: 'Professional spa services delivered directly to your Airbnb, villa, or residence.',
      studioTitle: 'Studio Services',
      studioDesc: 'Visit our downtown Cancun studio for a complete wellness experience.',
      energyTitle: 'Energy Work',
      energyDesc: 'Sound healing, Reiki, and group energy sessions to restore balance.',
      therapiesTitle: 'Therapeutic Sessions',
      therapiesDesc: 'Individual past life regression sessions with Priscila Vallejo, online or in Cancun.',
      eventsTitle: 'Events & Experiences',
      eventsDesc: 'Birthdays, bachelorette celebrations, Christmas gatherings, weddings, and group wellness experiences.',
      hostsSection: {
        title: 'Partner With Us',
        subtitle: 'For Airbnb & Vacation Rental Hosts',
        description: 'Enhance your guest experience with premium wellness services. We make it rewarding for you.',
        cta: 'Learn More'
      },
      philosophy: {
        title: 'Our Philosophy',
        subtitle: 'Holistic Wellness',
        description: 'We believe wellness is holistic. Our treatments integrate therapeutic massage techniques, natural and organic products, energy work, and mind-body relaxation.',
        values: ['Therapeutic Excellence', 'Natural Products', 'Energy Balance', 'Mind-Body Connection']
      },
      convenience: {
        title: 'We Come to You',
        description: 'Professional spa services delivered directly to your Airbnb, vacation rental, villa, or private residence. You can also visit our private studio in downtown Cancun.'
      },
      problems: {
        title: 'We Understand',
        subtitle: 'Your vacation should be stress-free',
        items: ['Travel stress', 'Muscle tension', 'Burnout', 'Limited spa access', 'Need for privacy']
      }
    },
    inHome: {
      title: 'In-Home Massage',
      subtitle: 'Luxury wellness delivered to your door',
      description: 'Experience professional spa services in the comfort of your Airbnb, vacation rental, villa, or private residence.',
      services: {
        relaxation: { name: 'Relaxation Massage', desc: 'Gentle strokes to release tension and promote deep relaxation.' },
        deepTissue: { name: 'Deep Tissue', desc: 'Focused pressure to release chronic muscle tension and knots.' },
        lymphatic: { name: 'Lymphatic Massage', desc: 'Gentle technique to stimulate lymph flow and reduce swelling.' },
        muscleRelease: { name: 'Muscle Release', desc: 'Targeted therapy to release muscle adhesions and restore mobility.' },
        thai: { name: 'Thai Massage', desc: 'Ancient stretching and pressure techniques for flexibility and energy flow.' },
        facialSculpting: { name: 'Facial Sculpting Massage', desc: 'Lift and tone facial muscles for a natural glow.' },
        organicFacial: { name: 'Organic Facial', desc: 'Deep cleansing facial using natural and organic products.' },
        bodyExfoliation: { name: 'Body Exfoliation', desc: 'Full body scrub to remove dead skin and reveal smooth skin.' }
      },
      downloadPrices: 'Download Price List'
    },
    studio: {
      title: 'Studio Services',
      subtitle: 'Your sanctuary in downtown Cancun',
      description: 'Visit our welcoming studio for a complete wellness experience with all our services available.',
      massageTitle: 'Massage Therapies',
      massages: {
        relaxation: 'Relaxation Massage',
        deepTissue: 'Deep Tissue',
        lymphatic: 'Lymphatic Massage',
        muscleRelease: 'Muscle Release',
        thai: 'Thai Massage',
        facialSculpting: 'Facial Sculpting'
      },
      techniques: {
        title: 'Enhancement Techniques',
        cupping: 'Cupping Therapy',
        hotStones: 'Hot Stones',
        stretching: 'Assisted Stretching',
        triggerPoints: 'Trigger Point Therapy'
      },
      skincare: {
        title: 'Skin Care',
        hydrafacial: 'Hydrafacial',
        organicFacial: 'Organic Facial'
      },
      bodyWorks: {
        title: 'Body Works',
        exfoliation: 'Body Exfoliation',
        woodTherapy: 'Wood Therapy'
      }
    },
    energy: {
      title: 'Energy Work',
      subtitle: 'Restore your energetic balance',
      description: 'Experience the healing power of sound and energy to calm your nervous system and restore inner peace.',
      soundHealing: {
        name: 'Sound Healing',
        desc: 'Immerse yourself in the healing vibrations of Tibetan singing bowls for deep relaxation and meditation.'
      },
      reiki: {
        name: 'Reiki',
        desc: 'Channel universal life energy to promote healing, reduce stress, and restore energetic balance.'
      },
      groupReiki: {
        name: 'Group Reiki',
        desc: 'Share the healing experience with friends or family in a group Reiki session.'
      }
    },
    regressions: {
      title: 'Past Life Regression',
      subtitle: 'Journey within for deep healing',
      description: 'Explore the depths of your consciousness with guided regression therapy and hypnosis sessions.',
      regression: {
        name: 'Regression Therapy',
        desc: 'Access past memories and experiences to understand and heal present challenges.'
      },
      hypnosis: {
        name: 'Hypnosis Sessions',
        desc: 'Use the power of suggestion to address habits, fears, and promote positive change.'
      }
    },
    janzu: {
      title: 'Janzu',
      subtitle: 'Water therapy for deep release',
      description: 'Float in warm water while being gently guided through movements that release physical and emotional tension.',
      benefits: ['Deep muscle relaxation', 'Emotional release', 'Nervous system reset', 'Meditative state', 'Spinal decompression']
    },
    events: {
      title: 'Events & Experiences',
      subtitle: 'Wellness for your special occasions',
      description: 'Create unforgettable wellness experiences for your group, corporate event, or special celebration.',
      services: {
        eventMassage: { name: 'Event Massage', desc: 'On-site massage services for corporate events, parties, and gatherings.' },
        groupSound: { name: 'Group Sound Healing', desc: 'Collective sound bath experience for groups of any size.' },
        groupReiki: { name: 'Group Reiki', desc: 'Shared energy healing session for teams or groups.' },
        wedding: { name: 'Wedding Wellness', desc: 'Pre-wedding spa services for the bride, groom, and wedding party.' }
      }
    },
    hosts: {
      title: 'For Airbnb & Vacation Rental Hosts',
      subtitle: 'Partner with ABBA Massage',
      description: 'Enhance your guest experience by recommending our premium wellness services. We make it rewarding for hosts who refer guests to us.',
      benefits: {
        title: 'Why Partner With Us',
        items: [
          'Certified professional therapists',
          'Bilingual staff (English & Spanish)',
          'Reliable and punctual service',
          'Credit card payments accepted',
          'Invoice available upon request',
          'Extensive experience with in-home services',
          'Spa parties and group bookings',
          'Guaranteed guest satisfaction'
        ]
      },
      cta: 'Become a Partner'
    },
    community: {
      title: 'Community',
      subtitle: 'Wellness for everyone',
      description: 'As part of our commitment to the community, we offer a complimentary weekly wellness session. Contact us for dates and availability.',
      initiatives: {
        title: 'Community Wellness',
        items: [
          'Complimentary weekly wellness sessions'
        ]
      }
    },
    store: {
      title: 'ABBA Store',
      subtitle: 'Bring wellness home',
      description: 'Curated wellness products to continue your journey at home.',
      categories: {
        supplements: 'Natural Supplements',
        cosmetics: 'Organic Cosmetics',
        aromatherapy: 'Aromatherapy',
        other: 'Wellness Items'
      },
      comingSoon: 'Products coming soon'
    },
    about: {
      title: 'About ABBA Massage',
      subtitle: 'Your wellness journey begins here',
      story: {
        title: 'Our Story',
        content: 'ABBA Massage was born from a passion for holistic wellness and a desire to bring luxury spa experiences to the beautiful beaches of Cancun. Our team of certified therapists combines traditional techniques with modern wellness practices to create transformative experiences.'
      },
      team: {
        title: 'Our Team',
        description: 'Meet our certified therapists dedicated to your wellbeing.'
      },
      values: {
        title: 'Our Values',
        items: ['Excellence', 'Integrity', 'Compassion', 'Sustainability']
      }
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'We\'d love to hear from you',
      whatsapp: 'WhatsApp',
      email: 'Email',
      phone: 'Call Us',
      location: 'Downtown Cancun, Mexico'
    },
    footer: {
      tagline: 'More than a massage. A journey to wellness.',
      quickLinks: 'Quick Links',
      services: 'Services',
      connect: 'Connect',
      payment: 'Payment Methods',
      paymentMethods: 'Credit cards, debit cards, cash (MXN & USD)',
      rights: 'All rights reserved.'
    },
    cta: {
      bookNow: 'Book Now',
      learnMore: 'Learn More',
      getStarted: 'Get Started',
      contact: 'Contact Us',
      download: 'Download'
    }
  },
  es: {
    nav: {
      home: 'Inicio',
      inHome: 'Masaje a Domicilio',
      studio: 'Servicios en Estudio',
      energy: 'Trabajo Energético',
      regressions: 'Regresiones e Hipnosis',
      janzu: 'Janzu',
      events: 'Eventos',
      hosts: 'Para Anfitriones',
      community: 'Comunidad',
      store: 'Tienda ABBA',
      about: 'Nosotros',
      contact: 'Contacto',
      book: 'Reservar',
      services: 'Servicios',
      therapies: 'Terapias'
    },
    hero: {
      tagline: 'Más que un masaje.',
      headline: 'Un viaje hacia el bienestar.',
      subheadline: 'Libera tensión. Calma tu sistema nervioso. Restaura el equilibrio.',
      cta: 'Reserva Tu Sesión'
    },
    home: {
      servicesTitle: 'Nuestras Experiencias',
      servicesSubtitle: 'Descubre el camino hacia tu bienestar',
      inHomeTitle: 'Masaje a Domicilio',
      inHomeDesc: 'Servicios de spa profesionales en tu Airbnb, villa o residencia.',
      studioTitle: 'Servicios en Estudio',
      studioDesc: 'Visita nuestro estudio en el centro de Cancún para una experiencia completa.',
      energyTitle: 'Trabajo Energético',
      energyDesc: 'Sanación con sonido, Reiki y sesiones grupales para restaurar el equilibrio.',
      therapiesTitle: 'Sesiones Terapéuticas',
      therapiesDesc: 'Sesiones individuales de regresión a vidas pasadas con Priscila Vallejo, online o en Cancún.',
      eventsTitle: 'Eventos y Experiencias',
      eventsDesc: 'Cumpleaños, despedidas de soltera, encuentros navideños, bodas y experiencias de bienestar en grupo.',
      hostsSection: {
        title: 'Asóciate Con Nosotros',
        subtitle: 'Para Anfitriones de Airbnb y Renta Vacacional',
        description: 'Mejora la experiencia de tus huéspedes con servicios de bienestar premium. Lo hacemos gratificante para ti.',
        cta: 'Más Información'
      },
      philosophy: {
        title: 'Nuestra Filosofía',
        subtitle: 'Bienestar Holístico',
        description: 'Creemos que el bienestar es holístico. Nuestros tratamientos integran técnicas de masaje terapéutico, productos naturales y orgánicos, trabajo energético y relajación mente-cuerpo.',
        values: ['Excelencia Terapéutica', 'Productos Naturales', 'Balance Energético', 'Conexión Mente-Cuerpo']
      },
      convenience: {
        title: 'Vamos a Ti',
        description: 'Servicios de spa profesionales entregados directamente a tu Airbnb, renta vacacional, villa o residencia privada. También puedes visitarnos en nuestro estudio privado en el centro de Cancún.'
      },
      problems: {
        title: 'Entendemos',
        subtitle: 'Tus vacaciones deben ser libres de estrés',
        items: ['Estrés de viaje', 'Tensión muscular', 'Agotamiento', 'Acceso limitado a spa', 'Necesidad de privacidad']
      }
    },
    inHome: {
      title: 'Masaje a Domicilio',
      subtitle: 'Bienestar de lujo en tu puerta',
      description: 'Experimenta servicios de spa profesionales en la comodidad de tu Airbnb, renta vacacional, villa o residencia privada.',
      services: {
        relaxation: { name: 'Masaje de Relajación', desc: 'Movimientos suaves para liberar tensión y promover relajación profunda.' },
        deepTissue: { name: 'Tejido Profundo', desc: 'Presión enfocada para liberar tensión muscular crónica y nudos.' },
        lymphatic: { name: 'Masaje Linfático', desc: 'Técnica suave para estimular el flujo linfático y reducir hinchazón.' },
        muscleRelease: { name: 'Liberación Muscular', desc: 'Terapia dirigida para liberar adhesiones musculares y restaurar movilidad.' },
        thai: { name: 'Masaje Tailandés', desc: 'Técnicas antiguas de estiramiento y presión para flexibilidad y flujo de energía.' },
        facialSculpting: { name: 'Masaje Facial Escultor', desc: 'Levanta y tonifica los músculos faciales para un brillo natural.' },
        organicFacial: { name: 'Facial Orgánico', desc: 'Limpieza facial profunda con productos naturales y orgánicos.' },
        bodyExfoliation: { name: 'Exfoliación Corporal', desc: 'Exfoliación de cuerpo completo para remover piel muerta y revelar piel suave.' }
      },
      downloadPrices: 'Descargar Lista de Precios'
    },
    studio: {
      title: 'Servicios en Estudio',
      subtitle: 'Tu santuario en el centro de Cancún',
      description: 'Visita nuestro acogedor estudio para una experiencia de bienestar completa con todos nuestros servicios disponibles.',
      massageTitle: 'Terapias de Masaje',
      massages: {
        relaxation: 'Masaje de Relajación',
        deepTissue: 'Tejido Profundo',
        lymphatic: 'Masaje Linfático',
        muscleRelease: 'Liberación Muscular',
        thai: 'Masaje Tailandés',
        facialSculpting: 'Facial Escultor'
      },
      techniques: {
        title: 'Técnicas Complementarias',
        cupping: 'Ventosas',
        hotStones: 'Piedras Calientes',
        stretching: 'Estiramiento Asistido',
        triggerPoints: 'Puntos Gatillo'
      },
      skincare: {
        title: 'Cuidado de la Piel',
        hydrafacial: 'Hydrafacial',
        organicFacial: 'Facial Orgánico'
      },
      bodyWorks: {
        title: 'Tratamientos Corporales',
        exfoliation: 'Exfoliación Corporal',
        woodTherapy: 'Maderoterapia'
      }
    },
    energy: {
      title: 'Trabajo Energético',
      subtitle: 'Restaura tu balance energético',
      description: 'Experimenta el poder curativo del sonido y la energía para calmar tu sistema nervioso y restaurar la paz interior.',
      soundHealing: {
        name: 'Sanación con Sonido',
        desc: 'Sumérgete en las vibraciones curativas de cuencos tibetanos para relajación profunda y meditación.'
      },
      reiki: {
        name: 'Reiki',
        desc: 'Canaliza energía vital universal para promover sanación, reducir estrés y restaurar el balance energético.'
      },
      groupReiki: {
        name: 'Reiki Grupal',
        desc: 'Comparte la experiencia de sanación con amigos o familia en una sesión grupal de Reiki.'
      }
    },
    regressions: {
      title: 'Regresiones e Hipnosis',
      subtitle: 'Viaja hacia adentro para sanación profunda',
      description: 'Explora las profundidades de tu consciencia con terapia de regresión guiada y sesiones de hipnosis.',
      regression: {
        name: 'Terapia de Regresión',
        desc: 'Accede a memorias y experiencias pasadas para entender y sanar desafíos presentes.'
      },
      hypnosis: {
        name: 'Sesiones de Hipnosis',
        desc: 'Usa el poder de la sugestión para abordar hábitos, miedos y promover cambios positivos.'
      }
    },
    janzu: {
      title: 'Janzu',
      subtitle: 'Terapia acuática para liberación profunda',
      description: 'Flota en agua cálida mientras eres guiado suavemente a través de movimientos que liberan tensión física y emocional.',
      benefits: ['Relajación muscular profunda', 'Liberación emocional', 'Reset del sistema nervioso', 'Estado meditativo', 'Descompresión espinal']
    },
    events: {
      title: 'Eventos y Experiencias',
      subtitle: 'Bienestar para tus ocasiones especiales',
      description: 'Crea experiencias de bienestar inolvidables para tu grupo, evento corporativo o celebración especial.',
      services: {
        eventMassage: { name: 'Masaje para Eventos', desc: 'Servicios de masaje en sitio para eventos corporativos, fiestas y reuniones.' },
        groupSound: { name: 'Sanación de Sonido Grupal', desc: 'Experiencia colectiva de baño de sonido para grupos de cualquier tamaño.' },
        groupReiki: { name: 'Reiki Grupal', desc: 'Sesión de sanación energética compartida para equipos o grupos.' },
        wedding: { name: 'Bienestar para Bodas', desc: 'Servicios de spa pre-boda para la novia, novio y cortejo nupcial.' }
      }
    },
    hosts: {
      title: 'Para Anfitriones de Airbnb y Renta Vacacional',
      subtitle: 'Asóciate con ABBA Massage',
      description: 'Mejora la experiencia de tus huéspedes recomendando nuestros servicios de bienestar premium. Lo hacemos gratificante para los anfitriones que nos refieren huéspedes.',
      benefits: {
        title: 'Por Qué Asociarte Con Nosotros',
        items: [
          'Terapeutas profesionales certificados',
          'Personal bilingüe (Inglés y Español)',
          'Servicio confiable y puntual',
          'Pagos con tarjeta de crédito aceptados',
          'Factura disponible bajo solicitud',
          'Amplia experiencia con servicios a domicilio',
          'Fiestas de spa y reservas grupales',
          'Satisfacción del huésped garantizada'
        ]
      },
      cta: 'Conviértete en Socio'
    },
    community: {
      title: 'Comunidad',
      subtitle: 'Bienestar para todos',
      description: 'Como parte de nuestro compromiso con la comunidad, ofrecemos una sesión de bienestar semanal gratuita. Contáctanos para conocer fechas y disponibilidad.',
      initiatives: {
        title: 'Bienestar Comunitario',
        items: [
          'Sesiones de bienestar semanales gratuitas'
        ]
      }
    },
    store: {
      title: 'Tienda ABBA',
      subtitle: 'Lleva el bienestar a casa',
      description: 'Productos de bienestar curados para continuar tu viaje en casa.',
      categories: {
        supplements: 'Suplementos Naturales',
        cosmetics: 'Cosméticos Orgánicos',
        aromatherapy: 'Aromaterapia',
        other: 'Artículos de Bienestar'
      },
      comingSoon: 'Productos próximamente'
    },
    about: {
      title: 'Sobre ABBA Massage',
      subtitle: 'Tu viaje de bienestar comienza aquí',
      story: {
        title: 'Nuestra Historia',
        content: 'ABBA Massage nació de una pasión por el bienestar holístico y el deseo de llevar experiencias de spa de lujo a las hermosas playas de Cancún. Nuestro equipo de terapeutas certificados combina técnicas tradicionales con prácticas modernas de bienestar para crear experiencias transformadoras.'
      },
      team: {
        title: 'Nuestro Equipo',
        description: 'Conoce a nuestros terapeutas certificados dedicados a tu bienestar.'
      },
      values: {
        title: 'Nuestros Valores',
        items: ['Excelencia', 'Integridad', 'Compasión', 'Sustentabilidad']
      }
    },
    contact: {
      title: 'Contáctanos',
      subtitle: 'Nos encantaría saber de ti',
      whatsapp: 'WhatsApp',
      email: 'Correo',
      phone: 'Llámanos',
      location: 'Centro de Cancún, México'
    },
    footer: {
      tagline: 'Más que un masaje. Un viaje hacia el bienestar.',
      quickLinks: 'Enlaces Rápidos',
      services: 'Servicios',
      connect: 'Conecta',
      payment: 'Métodos de Pago',
      paymentMethods: 'Tarjetas de crédito, débito, efectivo (MXN y USD)',
      rights: 'Todos los derechos reservados.'
    },
    cta: {
      bookNow: 'Reservar Ahora',
      learnMore: 'Más Información',
      getStarted: 'Comenzar',
      contact: 'Contáctanos',
      download: 'Descargar'
    }
  }
};

export default LanguageContext;
